import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Megaphone, Package, Pencil, Plus, ShieldCheck, Tags, Trash2, Wallet, X } from "lucide-react";
import { API } from "@/config/site";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { useStore } from "@/context/StoreContext";
import Reveal from "@/components/Reveal";
import AdminDashboard from "@/pages/AdminDashboard";

const inp =
  "w-full rounded-lg border border-white/10 bg-background px-3 py-2 text-sm outline-none transition-colors duration-200 focus:border-primary/50";

const Field = ({ label, children, testid }) => (
  <div data-testid={testid}>
    <p className="overline-label mb-2 !text-[10px]">{label}</p>
    {children}
  </div>
);

const EMPTY = {
  name: "", brand: "", description: "", image: "/cards/zeyinx-voucher.svg",
  category: "gaming", subcategory: "pc-gaming", region: "", country: "",
  redeemableCountries: [], currency: "USD", denominations: "", price: "",
  salePrice: "", platform: "", deliveryMethod: "Digital code sent by email",
  deliveryTime: "Usually instant; up to 5–10 minutes after payment confirmation",
  stock: "", status: "active", availability: true, redemptionInstructions: "",
  restrictions: "", terms: "", expiration: "", featured: false, supplier: "",
  supplierProductId: "",
  variants: [],
};

function CategoryManager({ store, onChanged }) {
  const [cats, setCats] = useState(null);
  const [editing, setEditing] = useState(null); // "new" | slug
  const [form, setForm] = useState({ name: "", sub: [] });
  const [newSub, setNewSub] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => {
    axios.get(`${API}/admin/categories`).then((r) => setCats(r.data)).catch(() => setCats([]));
  };
  useEffect(load, []);

  const startEdit = (c) => {
    setError("");
    setNewSub("");
    if (!c) {
      setForm({ name: "", sub: [] });
      setEditing("new");
    } else {
      setForm({ name: c.name, sub: c.sub.map((s) => ({ ...s })) });
      setEditing(c.slug);
    }
  };

  const addSub = () => {
    const name = newSub.trim();
    if (!name) return;
    setForm((f) => ({ ...f, sub: [...f.sub, { slug: "", name }] }));
    setNewSub("");
  };

  const save = async () => {
    setError("");
    setSaving(true);
    try {
      const payload = { name: form.name, sub: form.sub.map((s) => ({ name: s.name })) };
      if (editing === "new") await axios.post(`${API}/admin/categories`, payload);
      else await axios.put(`${API}/admin/categories/${editing}`, payload);
      toast.success(editing === "new" ? "Category created" : "Category updated");
      setEditing(null);
      load();
      onChanged();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (slug) => {
    try {
      await axios.delete(`${API}/admin/categories/${slug}`);
      toast.success("Category deleted");
      load();
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  if (cats === null) {
    return (
      <div className="flex justify-center pt-20">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div data-testid="admin-categories">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Categories power the storefront navigation. Changes apply without a redeploy.
        </p>
        <button
          data-testid="admin-new-category"
          onClick={() => startEdit(null)}
          className="flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition-transform duration-200 hover:scale-[1.02]"
        >
          <Plus className="h-4 w-4" /> New category
        </button>
      </div>

      {editing !== null && (
        <div className="mt-6 rounded-2xl border border-white/[0.1] bg-card p-6" data-testid="admin-category-editor">
          <Field label="Category name *">
            <input
              data-testid="admin-cat-name"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              className={inp}
              placeholder="e.g. Travel"
            />
          </Field>
          <div className="mt-5">
            <p className="overline-label mb-2 !text-[10px]">Subcategories</p>
            <div className="flex flex-col gap-2">
              {form.sub.map((s, i) => (
                <div key={i} className="flex items-center justify-between rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2 text-sm" data-testid={`admin-cat-sub-${i}`}>
                  <span>{s.name}</span>
                  <button
                    aria-label={`Remove ${s.name}`}
                    onClick={() => setForm((f) => ({ ...f, sub: f.sub.filter((_, j) => j !== i) }))}
                    className="text-muted-foreground transition-colors duration-150 hover:text-red-400"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
            <div className="mt-2 flex gap-2">
              <input
                data-testid="admin-cat-new-sub"
                value={newSub}
                onChange={(e) => setNewSub(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addSub())}
                placeholder="Add a subcategory…"
                className={inp}
              />
              <button
                data-testid="admin-cat-add-sub"
                onClick={addSub}
                className="shrink-0 rounded-lg border border-white/15 px-4 text-sm text-muted-foreground transition-colors duration-200 hover:border-white/30 hover:text-foreground"
              >
                Add
              </button>
            </div>
          </div>
          {error && (
            <p data-testid="admin-cat-error" className="mt-4 rounded-lg border border-red-500/25 bg-red-500/[0.07] px-3 py-2 text-xs text-red-300">
              {error}
            </p>
          )}
          <div className="mt-5 flex gap-3">
            <button
              data-testid="admin-cat-save"
              onClick={save}
              disabled={saving}
              className="flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground disabled:opacity-50"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              {editing === "new" ? "Create category" : "Save changes"}
            </button>
            <button
              data-testid="admin-cat-cancel"
              onClick={() => setEditing(null)}
              className="rounded-full border border-white/15 px-5 py-2.5 text-sm text-muted-foreground hover:text-foreground"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="mt-6 overflow-hidden rounded-2xl border border-white/[0.08]" data-testid="admin-categories-table">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-white/[0.08] text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              <th className="px-5 py-3.5 font-medium">Category</th>
              <th className="px-5 py-3.5 font-medium">Slug</th>
              <th className="px-5 py-3.5 font-medium">Subcategories</th>
              <th className="px-5 py-3.5 font-medium">Products</th>
              <th className="px-5 py-3.5 font-medium"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/[0.06]">
            {cats.map((c) => (
              <tr key={c.slug} className="transition-colors duration-150 hover:bg-white/[0.02]" data-testid={`admin-cat-row-${c.slug}`}>
                <td className="px-5 py-3.5 font-medium">{c.name}</td>
                <td className="px-5 py-3.5 font-mono text-xs text-muted-foreground">{c.slug}</td>
                <td className="px-5 py-3.5 text-xs text-muted-foreground">
                  {c.sub.map((s) => s.name).join(", ")}
                </td>
                <td className="px-5 py-3.5 text-muted-foreground">{c.product_count ?? 0}</td>
                <td className="px-5 py-3.5">
                  <div className="flex justify-end gap-2">
                    <button
                      data-testid={`admin-cat-edit-${c.slug}`}
                      onClick={() => startEdit(c)}
                      className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3.5 py-1.5 text-xs text-muted-foreground transition-colors duration-200 hover:border-white/30 hover:text-foreground"
                    >
                      <Pencil className="h-3 w-3" /> Edit
                    </button>
                    <button
                      data-testid={`admin-cat-delete-${c.slug}`}
                      onClick={() => remove(c.slug)}
                      aria-label={`Delete ${c.name}`}
                      className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3.5 py-1.5 text-xs text-muted-foreground transition-colors duration-200 hover:border-red-500/40 hover:text-red-400"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function BannerManager({ store, onChanged }) {
  const [banners, setBanners] = useState(null);
  const [targetSearch, setTargetSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const EMPTY = { market: "all", text: "", cta_label: "", cta_url: "", enabled: true, placement: "both", image_url: "", image_source: "Original ZEYINX artwork", rights_verified: false, start_at: "", end_at: "", priority: 0, offer_label: "", category_slug: "", discount_type: "none", discount_value: 0, currency: "all", target_products: [] };
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => axios.get(`${API}/admin/banners`).then((r) => setBanners(r.data)).catch(() => setBanners([]));
  useEffect(load, []);

  const startEdit = (b) => {
    setError("");
    setForm(b ? { ...EMPTY, ...b, enabled: Boolean(b.enabled), rights_verified: Boolean(b.rights_verified), priority: Number(b.priority || 0), discount_value: Number(b.discount_value || 0), currency: b.currency || "all", target_products: Array.isArray(b.target_products) ? b.target_products : [] } : EMPTY);
    setEditing(b ? b.id : "new");
  };

  const save = async () => {
    setError("");
    if (form.discount_type === "fixed" && (!form.currency || form.currency === "all")) {
      setError("Fixed promotions must specify the currency they are valid in.");
      return;
    }
    if (form.image_url && !form.rights_verified) {
      setError("Confirm that this image is original, licensed, or otherwise cleared for commercial use.");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, discount_value: Number(form.discount_value || 0), currency: form.currency || "all", target_products: Array.isArray(form.target_products) ? form.target_products : [], cta_url: form.cta_url || (form.category_slug ? (form.market !== "all" ? "/market/" + form.market + "/" + form.category_slug : "/cards?category=" + form.category_slug) : "/cards"), cta_label: form.cta_label || (form.category_slug ? "Shop offer" : "Shop now") };
      if (editing === "new") await axios.post(`${API}/admin/banners`, payload);
      else await axios.put(`${API}/admin/banners/${editing}`, payload);
      toast.success(editing === "new" ? "Promotion created" : "Promotion updated");
      setEditing(null); load(); onChanged();
    } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    try { await axios.delete(`${API}/admin/banners/${id}`); toast.success("Promotion deleted"); load(); onChanged(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (banners === null) return <div className="flex justify-center pt-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const marketName = (slug) => slug === "all" ? "All markets" : store.markets.find((m) => m.slug === slug)?.name || slug;
  return (
    <div data-testid="admin-banners">
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="text-sm text-muted-foreground">Manage homepage and market promotions. Images are shown only when you record their usage rights.</p>
          <p className="mt-1 text-xs text-muted-foreground">Safest choices: original ZEYINX artwork or AI-generated artwork with no third-party logos, characters, screenshots, or brand marks.</p>
        </div>
        <button data-testid="admin-new-banner" onClick={() => startEdit(null)} className="flex shrink-0 items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground"><Plus className="h-4 w-4" /> New promotion</button>
      </div>

      {editing !== null && (
        <div className="mt-6 max-w-3xl rounded-2xl border border-white/[0.1] bg-card p-6" data-testid="admin-banner-editor">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Placement"><select value={form.placement} onChange={(e) => setForm((f) => ({ ...f, placement: e.target.value }))} className={inp}><option value="market">Market pages</option><option value="home">Homepage</option><option value="both">Homepage + market pages</option></select></Field>
            <Field label="Market"><select data-testid="admin-banner-market" value={form.market} onChange={(e) => setForm((f) => ({ ...f, market: e.target.value }))} className={inp}><option value="all">All markets</option>{store.markets.map((m) => <option key={m.slug} value={m.slug}>{m.flag} {m.name}</option>)}</select></Field>
            <Field label="Priority"><input type="number" min="0" max="999" value={form.priority} onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))} className={inp} /><p className="mt-1 text-[11px] text-muted-foreground">Higher number = shown first when several promotions are live.</p></Field><Field label="Offer tag"><input value={form.offer_label} onChange={(e) => setForm((f) => ({ ...f, offer_label: e.target.value }))} placeholder="5% OFF · LIMITED TIME" className={inp} /></Field>
            <Field label="Enabled"><label className="flex h-[38px] cursor-pointer items-center gap-2 text-sm text-muted-foreground"><input type="checkbox" data-testid="admin-banner-enabled" checked={form.enabled} onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))} className="h-4 w-4 accent-[#00F0FF]" /> Publish promotion</label></Field>
          </div>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <Field label="Automatic discount">
              <select value={form.discount_type} onChange={(e) => setForm((f) => ({ ...f, discount_type: e.target.value }))} className={inp}>
                <option value="none">No automatic price discount</option>
                <option value="percent">Percentage discount</option>
                <option value="fixed">Fixed discount</option>
              </select>
            </Field>
            <Field label={form.discount_type === "percent" ? "Discount %" : "Discount amount"}>
              <input type="number" min="0" step="0.01" value={form.discount_value} onChange={(e) => setForm((f) => ({ ...f, discount_value: e.target.value }))} disabled={form.discount_type === "none"} className={inp} />
              <select value={form.currency} onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value }))} disabled={form.discount_type === "none"} className={inp}><option value="all">All currencies (percentage only)</option>{Object.keys(store.currencies || {}).map((code) => <option key={code} value={code}>{code}</option>)}</select>
            </Field>
          </div>
          <div className="mt-4">
            <Field label="Target gift cards">
              <div className="mb-2 flex gap-2"><input value={targetSearch} onChange={(e) => setTargetSearch(e.target.value)} placeholder="Search gift cards..." className={inp} /><button type="button" onClick={() => setForm((f) => ({ ...f, target_products: Object.values(store.productsMap || {}).filter((p) => !targetSearch.trim() || `${p.name} ${p.brand}`.toLowerCase().includes(targetSearch.trim().toLowerCase())).map((p) => p.slug) }))} className="shrink-0 rounded-lg border border-white/10 px-3 text-xs">Select shown</button><button type="button" onClick={() => setForm((f) => ({ ...f, target_products: [] }))} className="shrink-0 rounded-lg border border-white/10 px-3 text-xs">Clear</button></div>
              <div className="max-h-52 overflow-y-auto rounded-lg border border-white/10 bg-background p-2">
                <label className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-xs hover:bg-white/[0.04]">
                  <input type="checkbox" checked={form.target_products.length === 0} onChange={() => setForm((f) => ({ ...f, target_products: [] }))} className="h-3.5 w-3.5 accent-[#00F0FF]" />
                  All products matching the promotion category
                </label>
                {Object.values(store.productsMap || {}).filter((product) => !targetSearch.trim() || `${product.name} ${product.brand}`.toLowerCase().includes(targetSearch.trim().toLowerCase())).map((product) => (
                  <label key={product.slug} className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-xs text-muted-foreground hover:bg-white/[0.04]">
                    <input type="checkbox" checked={form.target_products.includes(product.slug)} onChange={(e) => setForm((f) => ({ ...f, target_products: e.target.checked ? [...f.target_products, product.slug] : f.target_products.filter((slug) => slug !== product.slug) }))} className="h-3.5 w-3.5 accent-[#00F0FF]" />
                    <span>{product.name}</span><span className="ml-auto text-[10px] opacity-60">{product.brand}</span>
                  </label>
                ))}
              </div>
              <p className="mt-1.5 text-[11px] text-muted-foreground">Select specific gift cards, or leave all unselected to target the selected category.</p>
            </Field>
          </div>
          <div className="mt-4 grid gap-4 sm:grid-cols-2"><Field label="Catalog category"><select value={form.category_slug} onChange={(e) => setForm((f) => ({ ...f, category_slug: e.target.value }))} className={inp}><option value="">All categories</option>{store.categories.map((cat) => <option key={cat.slug} value={cat.slug}>{cat.name}</option>)}</select></Field><Field label="Promotion headline *"><input data-testid="admin-banner-text" maxLength={200} value={form.text} onChange={(e) => setForm((f) => ({ ...f, text: e.target.value }))} placeholder="Gaming weekend — save on selected cards" className={inp} /></Field></div>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <Field label="CTA label"><input data-testid="admin-banner-cta-label" value={form.cta_label} onChange={(e) => setForm((f) => ({ ...f, cta_label: e.target.value }))} placeholder="Shop gaming" className={inp} /></Field>
            <Field label="CTA destination">
              <select data-testid="admin-banner-cta-url" value={form.cta_url || ""} onChange={(e) => setForm((f) => ({ ...f, cta_url: e.target.value }))} className={inp}>
                <option value="">Auto-select from category</option>
                <option value="/cards">All gift cards</option>
                {store.categories.map((cat) => <option key={cat.slug} value={form.market !== "all" ? `/market/${form.market}/${cat.slug}` : `/cards?category=${cat.slug}`}>{cat.name}</option>)}
              </select>
              <p className="mt-1.5 text-[11px] text-muted-foreground">Choose where the banner should take customers. The link is generated automatically.</p>
            </Field>
          </div>
          <div className="mt-4">
            <Field label="Upload banner image">
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                disabled={saving}
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  if (file.size > 5 * 1024 * 1024) { setError("Image must be 5 MB or smaller."); e.target.value = ""; return; }
                  const data = new FormData();
                  data.append("file", file);
                  setSaving(true); setError("");
                  try {
                    const res = await axios.post(`${API}/admin/banners/upload`, data);
                    setForm((f) => ({ ...f, image_url: res.data.image_url, image_source: "Licensed / owned asset", rights_verified: false }));
                    toast.success("Banner uploaded. Confirm the rights before publishing.");
                  } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
                  finally { setSaving(false); }
                }}
                className="block w-full rounded-lg border border-dashed border-white/15 bg-background px-3 py-3 text-sm"
              />
              <p className="mt-1.5 text-[11px] text-muted-foreground">JPG, PNG or WebP · max 5 MB. The file is stored outside GitHub so you can replace banners without a code deploy.</p>
            </Field>
            <div className="mt-3">
              <Field label="Image URL (optional)">
                <input value={form.image_url} onChange={(e) => setForm((f) => ({ ...f, image_url: e.target.value }))} placeholder="/media/promotions/..." className={inp} />
                <p className="mt-1.5 text-[11px] text-muted-foreground">You can also paste a hosted image URL. Only publish images whose commercial-use rights you have verified.</p>
              </Field>
            </div>
          </div>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <Field label="Image source"><select value={form.image_source} onChange={(e) => setForm((f) => ({ ...f, image_source: e.target.value }))} className={inp}><option>Original ZEYINX artwork</option><option>AI-generated artwork</option><option>Pexels</option><option>Unsplash</option><option>Pixabay</option><option>Licensed / owned asset</option></select></Field>
            <Field label="Rights verification"><label className="flex min-h-[38px] cursor-pointer items-center gap-2 text-sm text-muted-foreground"><input type="checkbox" checked={form.rights_verified} onChange={(e) => setForm((f) => ({ ...f, rights_verified: e.target.checked }))} className="h-4 w-4 accent-[#00F0FF]" /> I verified commercial-use rights and third-party marks</label></Field>
          </div>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <Field label="Start (optional)"><input type="datetime-local" value={form.start_at || ""} onChange={(e) => setForm((f) => ({ ...f, start_at: e.target.value }))} className={inp} /></Field>
            <Field label="End (optional)"><input type="datetime-local" value={form.end_at || ""} onChange={(e) => setForm((f) => ({ ...f, end_at: e.target.value }))} className={inp} /></Field>
          </div>
          <div className="mt-6 rounded-2xl border border-primary/20 bg-background/50 p-4">
            <div className="mb-3 flex items-center justify-between gap-3"><div><p className="overline-label !text-[10px]">Live preview</p><p className="mt-1 text-[11px] text-muted-foreground">Preview updates as you edit the promotion.</p></div><span className="rounded-full border border-white/10 px-2.5 py-1 text-[10px] text-muted-foreground">Priority {form.priority || 0}</span></div>
            <div className="overflow-hidden rounded-xl border border-white/10 bg-black">{form.image_url ? <div className="relative"><img src={form.image_url} alt="" className="block max-h-[360px] w-full object-contain" />{form.offer_label && <span className="absolute left-3 top-3 rounded-full bg-primary px-3 py-1 text-[10px] font-bold uppercase tracking-[0.12em] text-primary-foreground">{form.offer_label}</span>}</div> : <div className="flex min-h-40 items-center justify-center p-8 text-sm text-muted-foreground">Upload a banner image to preview it.</div>}</div>
            <div className="mt-3 flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground"><span>{form.placement === "both" ? "Homepage + market pages" : form.placement === "home" ? "Homepage" : "Market pages"}</span><span>· {marketName(form.market)}</span><span>· {form.discount_type === "none" ? "No automatic discount" : `${form.discount_type === "percent" ? form.discount_value + "%" : form.discount_value + " fixed"} automatic discount`}</span><span>· {form.target_products?.length ? `${form.target_products.length} selected gift card${form.target_products.length === 1 ? "" : "s"}` : "Category-wide target"}</span></div>
          </div>
          {error && <p data-testid="admin-banner-error" className="mt-4 rounded-lg border border-red-500/25 bg-red-500/[0.07] px-3 py-2 text-xs text-red-300">{error}</p>}
          <div className="mt-5 flex gap-3"><button data-testid="admin-banner-save" onClick={save} disabled={saving} className="flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground disabled:opacity-50">{saving && <Loader2 className="h-4 w-4 animate-spin" />}{editing === "new" ? "Create promotion" : "Save changes"}</button><button onClick={() => setEditing(null)} className="rounded-full border border-white/15 px-5 py-2.5 text-sm text-muted-foreground">Cancel</button></div>
        </div>
      )}

      {banners.length === 0 && editing === null ? <div className="mt-6 rounded-2xl border border-white/[0.08] bg-card p-10 text-center"><Megaphone className="mx-auto h-7 w-7 text-muted-foreground" /><p className="mt-3 text-sm text-muted-foreground">No promotions yet.</p></div> : (
        <div className="mt-6 overflow-hidden rounded-2xl border border-white/[0.08]"><table className="w-full text-left text-sm"><thead><tr className="border-b border-white/[0.08] text-[10px] uppercase tracking-[0.18em] text-muted-foreground"><th className="px-5 py-3.5 font-medium">Placement</th><th className="px-5 py-3.5 font-medium">Market</th><th className="px-5 py-3.5 font-medium">Promotion</th><th className="px-5 py-3.5 font-medium">Target / Offer</th><th className="px-5 py-3.5 font-medium">Priority</th><th className="px-5 py-3.5 font-medium">Rights</th><th className="px-5 py-3.5 font-medium">Status</th><th className="px-5 py-3.5"></th></tr></thead><tbody className="divide-y divide-white/[0.06]">{banners.map((b) => <tr key={b.id} data-testid={`admin-banner-row-${b.id}`} className="transition-colors hover:bg-white/[0.02]"><td className="px-5 py-3.5 text-xs text-muted-foreground">{b.placement}</td><td className="px-5 py-3.5 text-muted-foreground">{marketName(b.market)}</td><td className="max-w-sm px-5 py-3.5"><span className="block truncate">{b.text}</span>{b.image_url && <span className="text-[11px] text-muted-foreground">Image: {b.image_source || "unspecified"}</span>}</td><td className="px-5 py-3.5 text-xs"><span className="block">{b.discount_type && b.discount_type !== "none" ? `${b.discount_type === "percent" ? b.discount_value + "%" : b.discount_value + " fixed"} OFF` : "No automatic discount"}</span><span className="text-[10px] text-muted-foreground">{b.target_products?.length ? (() => { const names = b.target_products.map((slug) => store.productsMap?.[slug]?.name || slug).slice(0, 2); return `${names.join(", ")}${b.target_products.length > 2 ? ` +${b.target_products.length - 2} more` : ""}`; })() : (b.category_slug ? `${b.category_slug} category` : "All products")}</span></td><td className="px-5 py-3.5 text-xs font-mono">{b.priority || 0}</td><td className="px-5 py-3.5 text-xs">{b.image_url ? (b.rights_verified ? "Verified" : "Needs review") : "—"}</td><td className="px-5 py-3.5"><span className={`rounded-full border px-2.5 py-1 text-[10px] ${b.enabled ? "border-primary/40 bg-primary/10 text-primary" : "border-white/15 text-muted-foreground"}`}>{b.enabled ? "Live" : "Hidden"}</span></td><td className="px-5 py-3.5"><div className="flex justify-end gap-2"><button data-testid={`admin-banner-edit-${b.id}`} onClick={() => startEdit(b)} className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3.5 py-1.5 text-xs text-muted-foreground"><Pencil className="h-3 w-3" /> Edit</button><button data-testid={`admin-banner-delete-${b.id}`} onClick={() => remove(b.id)} className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3.5 py-1.5 text-xs text-muted-foreground hover:border-red-500/40 hover:text-red-400"><Trash2 className="h-3 w-3" /></button></div></td></tr>)}</tbody></table></div>
      )}
    </div>
  );
}
const ORDER_STATUS_LABELS = {
  awaiting_payment: ["Awaiting payment", "border-white/15 text-muted-foreground"],
  payment_received: ["Paid · awaiting fulfilment", "border-amber-500/40 bg-amber-500/10 text-amber-300"],
  code_preparing: ["Code being prepared", "border-white/15 bg-white/[0.04] text-muted-foreground"],
  code_delivered: ["Delivered", "border-primary/40 bg-primary/10 text-primary"],
  needs_support: ["Needs support", "border-red-500/40 bg-red-500/10 text-red-300"],
};

function OrdersManager() {
  const [orders, setOrders] = useState(null);
  const [fulfilling, setFulfilling] = useState(null);
  const [codeInputs, setCodeInputs] = useState([]);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => {
    axios.get(`${API}/admin/orders`).then((r) => setOrders(r.data)).catch(() => setOrders([]));
  };
  useEffect(load, []);

  const startFulfil = (o) => {
    setError("");
    const units = o.items.reduce((n, it) => n + it.quantity, 0);
    setCodeInputs(Array(units).fill(""));
    setFulfilling(o.order_id);
  };

  const fulfilUnits = (o) => {
    const units = [];
    o.items.forEach((it) => {
      for (let i = 0; i < it.quantity; i++) {
        units.push(`${it.name} · ${it.denomination} ${it.source_currency || it.currency || ""}`);
      }
    });
    return units;
  };

  const saveFulfil = async () => {
    setError("");
    setSaving(true);
    try {
      await axios.post(`${API}/admin/orders/${fulfilling}/fulfil`, { codes: codeInputs });
      toast.success("Order fulfilled — code emailed to the customer");
      setFulfilling(null);
      load();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (orderId, status) => {
    try {
      await axios.post(`${API}/admin/orders/${orderId}/status`, { delivery_status: status });
      toast.success("Status updated");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  if (orders === null) {
    return (
      <div className="flex justify-center pt-20">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  const fulfillingOrder = orders.find((o) => o.order_id === fulfilling);

  return (
    <div data-testid="admin-orders">
      <p className="text-sm text-muted-foreground">
        Paid orders land here and in your inbox. Verify the payment in your Razorpay dashboard,
        then enter the gift-card code(s) — the customer is emailed automatically.
      </p>

      {fulfillingOrder && (
        <div className="mt-6 max-w-2xl rounded-2xl border border-primary/30 bg-primary/[0.05] p-6" data-testid="admin-fulfil-editor">
          <p className="overline-label !text-primary/80">Fulfil order {fulfillingOrder.order_id}</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Customer: {fulfillingOrder.email} · one code per unit
          </p>
          <div className="mt-4 flex flex-col gap-3">
            {fulfilUnits(fulfillingOrder).map((label, i) => (
              <div key={i}>
                <p className="mb-1.5 text-[11px] text-muted-foreground">{label}</p>
                <input
                  data-testid={`fulfil-code-${i}`}
                  value={codeInputs[i]}
                  onChange={(e) =>
                    setCodeInputs((prev) => prev.map((c, j) => (j === i ? e.target.value : c)))
                  }
                  placeholder="Paste the gift-card code"
                  className={`${inp} font-mono`}
                />
              </div>
            ))}
          </div>
          {error && (
            <p data-testid="fulfil-error" className="mt-4 rounded-lg border border-red-500/25 bg-red-500/[0.07] px-3 py-2 text-xs text-red-300">
              {error}
            </p>
          )}
          <div className="mt-5 flex gap-3">
            <button
              data-testid="fulfil-save"
              onClick={saveFulfil}
              disabled={saving}
              className="flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground disabled:opacity-50"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              Send code to customer
            </button>
            <button
              data-testid="fulfil-cancel"
              onClick={() => setFulfilling(null)}
              className="rounded-full border border-white/15 px-5 py-2.5 text-sm text-muted-foreground hover:text-foreground"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {orders.length === 0 ? (
        <div className="mt-6 rounded-2xl border border-white/[0.08] bg-card p-10 text-center" data-testid="admin-orders-empty">
          <Package className="mx-auto h-7 w-7 text-muted-foreground" />
          <p className="mt-3 text-sm text-muted-foreground">No orders yet.</p>
        </div>
      ) : (
        <div className="mt-6 overflow-x-auto rounded-2xl border border-white/[0.08]" data-testid="admin-orders-table">
          <table className="w-full min-w-[820px] text-left text-sm">
            <thead>
              <tr className="border-b border-white/[0.08] text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                <th className="px-5 py-3.5 font-medium">Order</th>
                <th className="px-5 py-3.5 font-medium">Customer</th>
                <th className="px-5 py-3.5 font-medium">Items</th>
                <th className="px-5 py-3.5 font-medium">Total</th>
                <th className="px-5 py-3.5 font-medium">Status</th>
                <th className="px-5 py-3.5 font-medium"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/[0.06]">
              {orders.map((o) => {
                const badge = ORDER_STATUS_LABELS[o.delivery_status || o.status] || ORDER_STATUS_LABELS.awaiting_payment;
                const paid = o.status === "paid";
                const delivered = o.delivery_status === "code_delivered";
                return (
                  <tr key={o.order_id} className="transition-colors duration-150 hover:bg-white/[0.02]" data-testid={`admin-order-row-${o.order_id}`}>
                    <td className="px-5 py-3.5">
                      <span className="font-mono text-xs font-semibold">{o.order_id}</span>
                      <span className="block text-[11px] text-muted-foreground">
                        {new Date(o.created_at).toLocaleString()}
                      </span>
                    </td>
                    <td className="px-5 py-3.5 text-muted-foreground">{o.email}</td>
                    <td className="px-5 py-3.5 text-xs text-muted-foreground">
                      {o.items.map((it, i) => (
                        <span key={i} className="block">
                          {it.name} · {it.denomination} {it.product_currency} × {it.quantity}
                        </span>
                      ))}
                    </td>
                    <td className="px-5 py-3.5 font-mono text-xs">{o.total} {o.currency}</td>
                    <td className="px-5 py-3.5">
                      <span className={`rounded-full border px-2.5 py-1 text-[10px] ${badge[1]}`} data-testid={`order-status-${o.order_id}`}>
                        {badge[0]}
                      </span>
                    </td>
                    <td className="px-5 py-3.5">
                      <div className="flex justify-end gap-2">
                        {paid && !delivered && (
                          <>
                            <button
                              data-testid={`fulfil-start-${o.order_id}`}
                              onClick={() => startFulfil(o)}
                              className="rounded-full bg-primary px-3.5 py-1.5 text-xs font-semibold text-primary-foreground transition-transform duration-200 hover:scale-[1.03]"
                            >
                              Fulfil
                            </button>
                            <button
                              data-testid={`order-preparing-${o.order_id}`}
                              onClick={() => setStatus(o.order_id, "code_preparing")}
                              className="rounded-full border border-white/15 px-3.5 py-1.5 text-xs text-muted-foreground transition-colors duration-200 hover:text-foreground"
                            >
                              Preparing
                            </button>
                            <button
                              data-testid={`order-support-${o.order_id}`}
                              onClick={() => setStatus(o.order_id, "needs_support")}
                              className="rounded-full border border-white/15 px-3.5 py-1.5 text-xs text-muted-foreground transition-colors duration-200 hover:border-red-500/40 hover:text-red-400"
                            >
                              Needs support
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function AdminEcodes({ store }) {
  const [codes,setCodes]=useState([]), [products,setProducts]=useState([]), [product,setProduct]=useState(""), [text,setText]=useState(""), [notes,setNotes]=useState(""), [denomination,setDenomination]=useState(""), [sellPrice,setSellPrice]=useState("");
  const load=()=>{axios.get(`${API}/admin/ecodes`).then(r=>setCodes(r.data)).catch(()=>setCodes([])); axios.get(`${API}/admin/products`).then(r=>setProducts(r.data)).catch(()=>setProducts([]));};
  useEffect(load,[]);
  const add=async()=>{try{await axios.post(`${API}/admin/ecodes`,{product_slug:product,codes:text,notes,denomination:denomination||null,sell_price:sellPrice||null});toast.success("E-codes added");setText("");setNotes("");setDenomination("");setSellPrice("");load();}catch(e){toast.error(formatApiError(e.response?.data?.detail));}};
  return <div data-testid="admin-ecodes"><div className="grid gap-5 lg:grid-cols-[1fr_1.4fr]"><div className="rounded-2xl border border-white/[0.08] bg-card p-5"><p className="font-medium">Add E-Codes</p><p className="mt-1 text-xs text-muted-foreground">Paste one code per line or comma-separated.</p><select value={product} onChange={e=>setProduct(e.target.value)} className={`${inp} mt-5`}><option value="">Select product…</option>{products.map(p=><option key={p.slug} value={p.slug}>{p.name}</option>)}</select><select value={denomination} onChange={e=>setDenomination(e.target.value)} className={`${inp} mt-3`}><option value="">All / unspecified denomination</option>{(products.find(p=>p.slug===product)?.denominations||[]).map(d=><option key={d} value={d}>{store.fmtRaw(Number(d), products.find(p=>p.slug===product)?.currency||"USD")}</option>)}</select><input type="number" min="0" step="0.01" value={sellPrice} onChange={e=>setSellPrice(e.target.value)} placeholder="Selling price (e.g. 240, 250, 260)" className={`${inp} mt-3`}/><p className="mt-1 text-[11px] text-muted-foreground">Leave blank to use the card denomination as the selling price.</p><textarea value={text} onChange={e=>setText(e.target.value)} rows={7} placeholder="CODE-XXXX\nCODE-YYYY" className={`${inp} mt-3 font-mono text-xs`}/><input value={notes} onChange={e=>setNotes(e.target.value)} placeholder="Internal notes (optional)" className={`${inp} mt-3`}/><button onClick={add} disabled={!product||!text.trim()} className="mt-4 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground disabled:opacity-40">Add codes</button></div><div className="overflow-hidden rounded-2xl border border-white/[0.08]"><div className="border-b border-white/[0.08] p-5"><p className="font-medium">Inventory</p><p className="mt-1 text-xs text-muted-foreground">{codes.filter(c=>c.status==="available").length} available · {codes.filter(c=>c.status==="reserved").length} reserved · {codes.filter(c=>c.status==="sold").length} sold · {codes.length} total</p><div className="mt-3 grid grid-cols-3 gap-2 text-center"><div className="rounded-lg bg-emerald-500/[0.08] p-2"><p className="text-lg font-semibold text-emerald-300">{codes.filter(c=>c.status==="available").length}</p><p className="text-[9px] uppercase tracking-wider text-muted-foreground">Available</p></div><div className="rounded-lg bg-amber-500/[0.08] p-2"><p className="text-lg font-semibold text-amber-300">{codes.filter(c=>c.status==="reserved").length}</p><p className="text-[9px] uppercase tracking-wider text-muted-foreground">Reserved</p></div><div className="rounded-lg bg-red-500/[0.08] p-2"><p className="text-lg font-semibold text-red-300">{codes.filter(c=>c.status==="sold").length}</p><p className="text-[9px] uppercase tracking-wider text-muted-foreground">Sold</p></div></div></div><div className="max-h-[420px] overflow-auto">{codes.map(c=><div key={c.id} className="flex items-center justify-between gap-4 border-b border-white/[0.06] px-5 py-3 text-xs"><div><p className="font-mono">{c.code_masked || "••••••••"}</p><p className="mt-1 text-muted-foreground">{products.find(p=>p.slug===c.product_slug)?.name||c.product_slug}{c.denomination ? ` · ${store.fmtRaw(Number(c.denomination), products.find(p=>p.slug===c.product_slug)?.currency||"USD")}` : ""}{c.sell_price != null ? ` · Sell ${store.fmtRaw(Number(c.sell_price), products.find(p=>p.slug===c.product_slug)?.currency||"USD")}` : ""}</p></div><span className="rounded-full border border-primary/30 px-2 py-1 text-[10px] text-primary">{c.status}</span></div>)}{!codes.length&&<p className="p-8 text-center text-sm text-muted-foreground">No e-codes yet.</p>}</div></div></div></div>;
}

function CouponManager() {
  const store = useStore();
  const [items,setItems]=useState([]), [form,setForm]=useState({code:"",discount_type:"percent",discount_value:"",min_order:"",max_uses:"",starts_at:"",ends_at:"",enabled:true,audience:"all",market:"all",category_slug:"",product_slugs:"",banner_label:"",currency:"all"});
  const load=()=>axios.get(`${API}/admin/coupons`).then(r=>setItems(r.data)).catch(()=>setItems([])); useEffect(load,[]);
  const set=(k,v)=>setForm(f=>({...f,[k]:v}));
  const add=async()=>{try{if(form.discount_type==="fixed"&&form.currency==="all") throw new Error("Fixed coupons must specify a currency."); await axios.post(`${API}/admin/coupons`,form);toast.success("Coupon created");setForm({...form,code:"",discount_value:"",min_order:"",max_uses:""});load();}catch(e){toast.error(formatApiError(e.response?.data?.detail));}};
  const remove=async(id)=>{await axios.delete(`${API}/admin/coupons/${id}`);load();toast.success("Coupon deleted");};
  return <div data-testid="admin-coupons"><div className="rounded-2xl border border-white/[0.08] bg-card p-5"><p className="font-medium">Create discount coupon</p><div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4"><input value={form.code} onChange={e=>set("code",e.target.value.toUpperCase())} placeholder="SAVE10" className={inp}/><select value={form.discount_type} onChange={e=>set("discount_type",e.target.value)} className={inp}><option value="percent">Percentage</option><option value="fixed">Fixed amount</option></select><input type="number" value={form.discount_value} onChange={e=>set("discount_value",e.target.value)} placeholder="Discount value" className={inp}/><input type="number" value={form.min_order} onChange={e=>set("min_order",e.target.value)} placeholder="Minimum order" className={inp}/><input type="number" value={form.max_uses} onChange={e=>set("max_uses",e.target.value)} placeholder="Max uses (optional)" className={inp}/><select value={form.audience} onChange={e=>set("audience",e.target.value)} className={inp}><option value="all">Everyone</option><option value="new_users">New users only</option></select><select value={form.market} onChange={e=>set("market",e.target.value)} className={inp}><option value="all">All markets</option>{store.markets.map(m=><option key={m.slug} value={m.slug}>{m.flag} {m.name}</option>)}</select><select value={form.currency} onChange={e=>set("currency",e.target.value)} className={inp}><option value="all">All currencies (percentage only)</option>{Object.keys(store.currencies || {}).map(code=><option key={code} value={code}>{code}</option>)}</select><input value={form.category_slug} onChange={e=>set("category_slug",e.target.value)} placeholder="Category slug (optional)" className={inp}/><input value={form.product_slugs} onChange={e=>set("product_slugs",e.target.value)} placeholder="Product slugs, comma-separated" className={inp}/><input value={form.banner_label} onChange={e=>set("banner_label",e.target.value)} placeholder="Display label e.g. 5% OFF" className={inp}/><input type="datetime-local" value={form.starts_at} onChange={e=>set("starts_at",e.target.value)} className={inp}/><input type="datetime-local" value={form.ends_at} onChange={e=>set("ends_at",e.target.value)} className={inp}/></div><button onClick={add} disabled={!form.code||!form.discount_value} className="mt-4 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground disabled:opacity-40">Create coupon</button></div><div className="mt-5 overflow-hidden rounded-2xl border border-white/[0.08]"><table className="w-full text-left text-sm"><thead><tr className="border-b border-white/[0.08] text-[10px] uppercase tracking-wider text-muted-foreground"><th className="px-5 py-3">Code</th><th className="px-5 py-3">Discount</th><th className="px-5 py-3">Audience</th><th className="px-5 py-3">Uses</th><th className="px-5 py-3"></th></tr></thead><tbody>{items.map(c=><tr key={c.id} className="border-b border-white/[0.06]"><td className="px-5 py-3 font-mono">{c.code}</td><td className="px-5 py-3">{c.discount_type==="percent"?c.discount_value+"%":store.fmtRaw(Number(c.discount_value||0), c.currency || "USD")}</td><td className="px-5 py-3 text-xs text-muted-foreground">{c.audience==="new_users"?"New users":"Everyone"}{c.category_slug?` · ${c.category_slug}`:""}{c.currency && c.currency!=="all"?` · ${c.currency}`:""}</td><td className="px-5 py-3 text-muted-foreground">{c.uses}{c.max_uses?"/"+c.max_uses:""}</td><td className="px-5 py-3 text-right"><button onClick={()=>remove(c.id)} className="text-xs text-red-300">Delete</button></td></tr>)}</tbody></table>{!items.length&&<p className="p-8 text-center text-sm text-muted-foreground">No coupons yet.</p>}</div></div>;
}

function StoreSettingsManager() {
  const [form,setForm]=useState({store_name:"ZEYINX",announcement:"",support_email:"support@zeyinx.com",maintenance_mode:false}), [saving,setSaving]=useState(false);
  useEffect(()=>{axios.get(`${API}/admin/settings/store`).then(r=>setForm(r.data)).catch(()=>{});},[]);
  const set=(k,v)=>setForm(f=>({...f,[k]:v}));
  const save=async()=>{setSaving(true);try{await axios.put(`${API}/admin/settings/store`,form);toast.success("Store settings saved");}catch(e){toast.error(formatApiError(e.response?.data?.detail));}finally{setSaving(false);}};
  return <div className="max-w-3xl rounded-2xl border border-white/[0.08] bg-card p-6" data-testid="admin-store-settings"><div className="grid gap-5 sm:grid-cols-2"><Field label="Store name"><input value={form.store_name} onChange={e=>set("store_name",e.target.value)} className={inp}/></Field><Field label="Support email"><input value={form.support_email} onChange={e=>set("support_email",e.target.value)} className={inp}/></Field></div><div className="mt-5"><Field label="Store announcement"><textarea value={form.announcement} onChange={e=>set("announcement",e.target.value)} rows={4} placeholder="Announcement shown to customers" className={inp}/></Field></div><label className="mt-5 flex items-center gap-3 text-sm text-muted-foreground"><input type="checkbox" checked={form.maintenance_mode} onChange={e=>set("maintenance_mode",e.target.checked)} className="h-4 w-4 accent-[#00F0FF]"/> Maintenance mode</label><button onClick={save} disabled={saving} className="mt-6 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground disabled:opacity-40">{saving?"Saving…":"Save store settings"}</button></div>;
}

function PaymentManager() {
  const [settings, setSettings] = useState(null);
  const [keyId, setKeyId] = useState("");
  const [keySecret, setKeySecret] = useState("");
  const [webhookSecret, setWebhookSecret] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const load = () => {
    axios
      .get(`${API}/admin/settings/payment`)
      .then((r) => {
        setSettings(r.data);
        setKeyId(r.data.key_id || "");
      })
      .catch(() => setSettings({ configured: false }));
  };
  useEffect(load, []);

  const save = async () => {
    setError("");
    setSaving(true);
    try {
      await axios.put(`${API}/admin/settings/payment`, {
        key_id: keyId.trim(),
        ...(keySecret.trim() ? { key_secret: keySecret.trim() } : {}),
        ...(webhookSecret.trim() ? { webhook_secret: webhookSecret.trim() } : {}),
      });
      toast.success("Payment settings saved");
      setKeySecret("");
      setWebhookSecret("");
      load();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (settings === null) {
    return (
      <div className="flex justify-center pt-20">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  const mode = keyId.startsWith("rzp_live_") ? "Live" : keyId.startsWith("rzp_test_") ? "Test" : null;

  return (
    <div className="max-w-2xl" data-testid="admin-payments">
      <div className="flex items-center justify-between gap-4">
        <p className="text-sm text-muted-foreground">
          Zeyinx accepts payments via Razorpay (UPI and cards for India, cards for other
          currencies). Paste your keys from the Razorpay Dashboard → Settings → API Keys.
        </p>
        <span
          data-testid="payment-status-badge"
          className={`shrink-0 rounded-full border px-3 py-1 text-[10px] font-medium ${
            settings.configured
              ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
              : "border-amber-500/40 bg-amber-500/10 text-amber-300"
          }`}
        >
          {settings.configured ? `Configured${mode ? ` · ${mode} mode` : ""}` : "Not configured"}
        </span>
      </div>

      <div className="mt-6 flex flex-col gap-5 rounded-2xl border border-white/[0.1] bg-card p-6">
        <Field label="Key ID *" testid="payment-key-id-field">
          <input
            data-testid="payment-key-id-input"
            value={keyId}
            onChange={(e) => setKeyId(e.target.value)}
            placeholder="rzp_test_… or rzp_live_…"
            className={`${inp} font-mono`}
          />
        </Field>
        <Field
          label={`Key secret ${settings.key_secret_masked ? `(saved: ${settings.key_secret_masked} — leave blank to keep)` : "*"}`}
          testid="payment-key-secret-field"
        >
          <input
            data-testid="payment-key-secret-input"
            type="password"
            value={keySecret}
            onChange={(e) => setKeySecret(e.target.value)}
            placeholder={settings.key_secret_masked || "Paste your key secret"}
            className={`${inp} font-mono`}
            autoComplete="new-password"
          />
        </Field>
        <Field
          label={`Webhook secret (optional${settings.webhook_secret_set ? " — saved" : ""})`}
          testid="payment-webhook-secret-field"
        >
          <input
            data-testid="payment-webhook-secret-input"
            type="password"
            value={webhookSecret}
            onChange={(e) => setWebhookSecret(e.target.value)}
            placeholder={settings.webhook_secret_set ? "••••••••" : "Only if you set up a webhook"}
            className={`${inp} font-mono`}
            autoComplete="new-password"
          />
          <p className="mt-2 text-[11px] leading-relaxed text-muted-foreground/70">
            Optional backup confirmation: in Razorpay Dashboard → Webhooks, add{" "}
            <span className="font-mono text-foreground/80">{API}/webhook/razorpay</span> for the{" "}
            <span className="font-mono">payment.captured</span> event and paste the same secret
            here.
          </p>
        </Field>

        {error && (
          <p data-testid="payment-settings-error" className="rounded-lg border border-red-500/25 bg-red-500/[0.07] px-3 py-2 text-xs text-red-300">
            {error}
          </p>
        )}

        <div>
          <button
            data-testid="payment-save-button"
            onClick={save}
            disabled={saving || !keyId.trim() || (!keySecret.trim() && !settings.configured)}
            className="flex items-center gap-2 rounded-full bg-primary px-6 py-2.5 text-sm font-semibold text-primary-foreground transition-transform duration-200 hover:scale-[1.02] disabled:opacity-50 disabled:hover:scale-100"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            Save payment settings
          </button>
          <p className="mt-3 text-[11px] leading-relaxed text-muted-foreground/70">
            Keys are stored on the server and never shown in full. Use <span className="font-mono">rzp_test_</span>{" "}
            keys while testing, then switch to <span className="font-mono">rzp_live_</span> to accept real money.
          </p>
        </div>
      </div>
    </div>
  );
}

export default function Admin() {
  const { user, openAuth } = useAuth();
  const store = useStore();
  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get("tab") || (window.location.pathname === "/admin" ? "overview" : "products"));
  const [products, setProducts] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [globalConfirm, setGlobalConfirm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [catalogLoadError, setCatalogLoadError] = useState("");

  const isAdmin = user?.role === "admin";
  const selectTab = (id) => { setTab(id); const url = new URL(window.location.href); url.searchParams.set("tab", id); window.history.replaceState({}, "", url); window.scrollTo({ top: 0, behavior: "auto" }); };

  const load = () => {
    setCatalogLoadError("");
    axios.get(`${API}/admin/products`).then((r) => setProducts(Array.isArray(r.data) ? r.data : [])).catch((err) => {
      setProducts(Object.values(store.productsMap || {}));
      setCatalogLoadError(err.response?.data?.detail || "Admin catalog could not be loaded. Showing the live storefront catalog.");
    });
  };

  useEffect(() => {
    if (isAdmin) load();
  }, [isAdmin]);

  if (user === undefined) {
    return (
      <div className="neon-page"><div className="vault-container flex justify-center pt-32">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div></div>
    );
  }

  if (!user || !isAdmin) {
    return (
      <div className="vault-container flex flex-col items-center pt-28 text-center" data-testid="admin-signin-prompt">
        <ShieldCheck className="h-8 w-8 text-muted-foreground" />
        <h1 className="mt-6 font-display text-2xl font-semibold tracking-tight">Admin access required</h1>
        <p className="mt-2 text-sm text-muted-foreground">Sign in with an admin account to manage the catalog.</p>
        {!user && (
          <button
            data-testid="admin-sign-in-button"
            onClick={() => openAuth("signin")}
            className="mt-8 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground"
          >
            Sign in
          </button>
        )}
      </div>
    );
  }

  const startEdit = (p) => {
    setError("");
    setGlobalConfirm(false);
    if (!p) {
      setForm(EMPTY);
      setEditing("new");
      return;
    }
    setForm({
      ...EMPTY,
      ...p,
      country: p.country || "",
      redeemableCountries: p.redeemableCountries || [],
      denominations: (p.denominations || []).join(", "),
      price: p.price ?? "",
      salePrice: p.salePrice ?? "",
      stock: p.stock ?? "",
      variants: Array.isArray(p.variants) ? p.variants : [],
    });
    setEditing(p.slug);
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleCountry = (code) =>
    setForm((f) => ({
      ...f,
      redeemableCountries: f.redeemableCountries.includes(code)
        ? f.redeemableCountries.filter((c) => c !== code)
        : [...f.redeemableCountries, code],
    }));

  const save = async () => {
    setError("");
    if (form.region === "global" && !globalConfirm) {
      setError("Confirm that this product is genuinely supported in all listed redeemable countries before classifying it as Global.");
      return;
    }
    const payload = {
      ...form,
      country: form.country || null,
      denominations: form.denominations
        .split(",")
        .map((d) => parseFloat(d.trim()))
        .filter((d) => !Number.isNaN(d)),
      price: form.price === "" ? null : parseFloat(form.price),
      salePrice: form.salePrice === "" ? null : parseFloat(form.salePrice),
      stock: form.stock === "" ? null : parseInt(form.stock, 10),
      variants: (form.denominations || "")
        .split(",")
        .map((d) => Number(d.trim()))
        .filter((d) => Number.isFinite(d) && d >= 100)
        .map((denomination) => {
          const existing = (form.variants || []).find((v) => Number(v.denomination) === denomination);
          return {
            denomination,
            stock: existing?.stock ?? 0,
            salePrice: existing?.salePrice ?? "",
          };
        }),
    };
    setSaving(true);
    try {
      if (editing === "new") await axios.post(`${API}/admin/products`, payload);
      else await axios.put(`${API}/admin/products/${editing}`, payload);
      toast.success(editing === "new" ? "Product created" : "Product updated");
      setEditing(null);
      load();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="vault-container pt-0 sm:pt-1" data-testid="admin-products-page">
      <Reveal>
        <div className="flex flex-col gap-4">
          <div>
            <p className="overline-label">Admin · Store Management</p>
            <h1 className="mt-1 font-display text-3xl font-semibold tracking-tighter sm:text-4xl">Store control center</h1>
            <p className="mt-1 max-w-2xl text-sm text-muted-foreground">Manage your catalog, promotions, fulfilment inventory, discounts and storefront settings from one workspace.</p>
          </div>
          <div className="w-full overflow-x-auto rounded-2xl border border-white/10 bg-white/[0.03] p-1" role="tablist">
            {[
              ["overview", "Overview", ShieldCheck],
              ["products", "Products", Package],
              ["categories", "Categories", Tags],
              ["banners", "Promotions & Offers", Megaphone],
              ["orders", "Orders", Package],
              ["payments", "Payments", Wallet],
              ["ecodes", "E-Codes", Package],
              ["coupons", "Discounts & Coupons", Tags],
              ["settings", "Store Settings", ShieldCheck],
            ].map(([id, label, Icon]) => (
              <button
                key={id}
                role="tab"
                aria-selected={tab === id}
                data-testid={`admin-tab-${id}`}
                onClick={() => selectTab(id)}
                className={`flex shrink-0 items-center gap-2 rounded-xl px-4 py-2 text-sm transition-colors duration-200 ${
                  tab === id ? "bg-white/[0.09] text-foreground" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <Icon className="h-3.5 w-3.5" /> {label}
              </button>
            ))}
          </div>
        </div>
      </Reveal>

      {tab === "overview" && (<div className="mt-4"><AdminDashboard embedded /></div>)}

      {tab === "categories" && (
        <div className="mt-7">
          <CategoryManager store={store} onChanged={() => store.reloadMeta?.()} />
        </div>
      )}

      {tab === "banners" && (
        <div className="mt-7">
          <BannerManager store={store} onChanged={() => store.reloadMeta?.()} />
        </div>
      )}

      {tab === "orders" && (
        <div className="mt-10">
          <OrdersManager />
        </div>
      )}

      {tab === "ecodes" && <div className="mt-4"><AdminEcodes store={store} /></div>}
      {tab === "coupons" && <div className="mt-4"><CouponManager /></div>}
      {tab === "settings" && <div className="mt-4"><StoreSettingsManager /></div>}

      {tab === "payments" && (
        <div className="mt-10">
          <PaymentManager />
        </div>
      )}

      {tab === "products" && editing === null && (
        <>
          <div className="mt-4 flex items-center justify-between gap-4"><p className="text-sm text-muted-foreground">Manage the products currently visible in your storefront.</p>
            <button
              data-testid="admin-new-product"
              onClick={() => startEdit(null)}
              className="flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition-transform duration-200 hover:scale-[1.02]"
            >
              <Plus className="h-4 w-4" /> New product
            </button>
          </div>
          {catalogLoadError && <div className="mb-4 rounded-xl border border-amber-500/20 bg-amber-500/[0.06] px-4 py-3 text-xs text-amber-200">{catalogLoadError}</div>}
          {products === null ? (
            <div className="flex justify-center pt-20">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : products.length === 0 ? (
            <div className="mt-16 rounded-2xl border border-white/[0.08] bg-card p-10 text-center" data-testid="admin-empty">
              <Package className="mx-auto h-7 w-7 text-muted-foreground" />
              <p className="mt-3 text-sm text-muted-foreground">No products yet.</p>
            </div>
          ) : (
            <div className="mt-6 overflow-x-auto rounded-2xl border border-white/[0.08]" data-testid="admin-products-table">
              <table className="w-full min-w-[760px] text-left text-sm">
                <thead>
                  <tr className="border-b border-white/[0.08] text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                    <th className="px-5 py-3.5 font-medium">Product</th>
                    <th className="px-5 py-3.5 font-medium">Region</th>
                    <th className="px-5 py-3.5 font-medium">Country</th>
                    <th className="px-5 py-3.5 font-medium">Redeemable</th>
                    <th className="px-5 py-3.5 font-medium">Currency</th>
                    <th className="px-5 py-3.5 font-medium">Status</th>
                    <th className="px-5 py-3.5 font-medium"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/[0.06]">
                  {products.map((p) => (
                    <tr key={p.slug} className="transition-colors duration-150 hover:bg-white/[0.02]" data-testid={`admin-row-${p.slug}`}>
                      <td className="px-5 py-3.5">
                        <span className="font-medium">{p.name}</span>
                        <span className="block text-[11px] text-muted-foreground">{p.brand} · {store.categoryName(p.category)}</span>
                      </td>
                      <td className="px-5 py-3.5 text-muted-foreground">{store.markets.find((m) => m.slug === p.region)?.name || p.region}</td>
                      <td className="px-5 py-3.5 text-muted-foreground">{p.country ? `${store.countryFlag(p.country)} ${store.countryName(p.country)}` : "—"}</td>
                      <td className="px-5 py-3.5 text-muted-foreground">{(p.redeemableCountries || []).length} countr{(p.redeemableCountries || []).length === 1 ? "y" : "ies"}</td>
                      <td className="px-5 py-3.5 font-mono text-xs">{p.currency}</td>
                      <td className="px-5 py-3.5">
                        <span className={`rounded-full border px-2.5 py-1 text-[10px] ${p.status === "active" && p.availability ? "border-primary/40 bg-primary/10 text-primary" : "border-white/15 text-muted-foreground"}`}>
                          {p.status === "active" ? (p.availability ? "Active" : "Unavailable") : "Inactive"}
                        </span>
                      </td>
                      <td className="px-5 py-3.5 text-right">
                        <button
                          data-testid={`admin-edit-${p.slug}`}
                          onClick={() => startEdit(p)}
                          className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3.5 py-1.5 text-xs text-muted-foreground transition-colors duration-200 hover:border-white/30 hover:text-foreground"
                        >
                          <Pencil className="h-3 w-3" /> Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {tab === "products" && editing !== null && (
        <div className="mt-10 max-w-3xl" data-testid="admin-product-editor">
          <h2 className="font-display text-2xl font-semibold tracking-tight">
            {editing === "new" ? "New product" : `Edit: ${form.name}`}
          </h2>

          <div className="mt-8 grid gap-4 sm:grid-cols-2">
            <Field label="Product Name *" testid="admin-f-name">
              <input value={form.name} onChange={(e) => set("name", e.target.value)} className={inp} />
            </Field>
            <Field label="Brand *" testid="admin-f-brand">
              <input value={form.brand} onChange={(e) => set("brand", e.target.value)} className={inp} />
            </Field>
            <Field label="Category *">
              <select
                data-testid="admin-f-category"
                value={form.category}
                onChange={(e) => setForm((f) => ({ ...f, category: e.target.value, subcategory: store.categories.find((c) => c.slug === e.target.value)?.sub[0]?.slug || "" }))}
                className={inp}
              >
                {store.categories.map((c) => (
                  <option key={c.slug} value={c.slug}>{c.name}</option>
                ))}
              </select>
            </Field>
            <Field label="Subcategory">
              <select data-testid="admin-f-subcategory" value={form.subcategory} onChange={(e) => set("subcategory", e.target.value)} className={inp}>
                {(store.categories.find((c) => c.slug === form.category)?.sub || []).map((s) => (
                  <option key={s.slug} value={s.slug}>{s.name}</option>
                ))}
              </select>
            </Field>
            <Field label="Region (market) *" testid="admin-f-region">
              <select
                data-testid="admin-f-region-select"
                value={form.region}
                onChange={(e) => setForm((f) => ({ ...f, region: e.target.value, country: e.target.value === "global" ? "" : f.country }))}
                className={inp}
              >
                <option value="">Select market…</option>
                {store.markets.map((m) => (
                  <option key={m.slug} value={m.slug}>{m.flag} {m.name}</option>
                ))}
              </select>
            </Field>
            <Field label="Country *">
              <select
                data-testid="admin-f-country"
                value={form.country}
                onChange={(e) => set("country", e.target.value)}
                disabled={form.region === "global"}
                className={`${inp} disabled:opacity-40`}
              >
                <option value="">{form.region === "global" ? "— (Global)" : "Select country…"}</option>
                {store.countries.map((c) => (
                  <option key={c.code} value={c.code}>{c.flag} {c.name}</option>
                ))}
              </select>
            </Field>
            <Field label="Currency *">
              <select data-testid="admin-f-currency" value={form.currency} onChange={(e) => set("currency", e.target.value)} className={inp}>
                {Object.keys(store.currencies).map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </Field>
            <Field label="Denominations * (comma separated, native currency)">
              <input data-testid="admin-f-denominations" value={form.denominations} onChange={(e) => set("denominations", e.target.value)} placeholder="100, 250, 500, 1000" className={inp} />
            </Field>
            <div className="sm:col-span-2 rounded-2xl border border-white/[0.08] bg-white/[0.02] p-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-medium">Inventory & selling price by denomination</p>
                  <p className="mt-1 text-[11px] text-muted-foreground">No e-code is required. Set manual stock here. Minimum denomination is 100.</p>
                </div>
              </div>
              <div className="mt-4 space-y-2">
                {(form.denominations || "")
                  .split(",")
                  .map((d) => Number(d.trim()))
                  .filter((d) => Number.isFinite(d) && d >= 100)
                  .map((denomination) => {
                    const v = (form.variants || []).find((x) => Number(x.denomination) === denomination) || { denomination, stock: 0, salePrice: "" };
                    return (
                      <div key={denomination} className="grid grid-cols-[1fr_1fr_1fr] gap-2 items-end">
                        <div>
                          <p className="mb-1 text-[10px] uppercase tracking-wider text-muted-foreground">Value</p>
                          <div className="rounded-lg border border-white/10 bg-background px-3 py-2 text-sm">{store.fmtRaw(denomination, form.currency)}</div>
                        </div>
                        <div>
                          <p className="mb-1 text-[10px] uppercase tracking-wider text-muted-foreground">Stock</p>
                          <input type="number" min="0" step="1" value={v.stock ?? 0} onChange={(e) => setForm((f) => ({ ...f, variants: [...(f.variants || []).filter((x) => Number(x.denomination) !== denomination), { denomination, stock: e.target.value, salePrice: v.salePrice ?? "" }] }))} className={inp} />
                        </div>
                        <div>
                          <p className="mb-1 text-[10px] uppercase tracking-wider text-muted-foreground">Sell price</p>
                          <input type="number" min="0" step="0.01" value={v.salePrice ?? ""} placeholder={String(denomination)} onChange={(e) => setForm((f) => ({ ...f, variants: [...(f.variants || []).filter((x) => Number(x.denomination) !== denomination), { denomination, stock: v.stock ?? 0, salePrice: e.target.value }] }))} className={inp} />
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
            <Field label="Price (optional override)">
              <input value={form.price} onChange={(e) => set("price", e.target.value)} inputMode="decimal" className={inp} />
            </Field>
            <Field label="Sale Price (optional)">
              <input value={form.salePrice} onChange={(e) => set("salePrice", e.target.value)} inputMode="decimal" className={inp} />
            </Field>
            <Field label="Platform">
              <input data-testid="admin-f-platform" value={form.platform} onChange={(e) => set("platform", e.target.value)} className={inp} />
            </Field>
            <Field label="Image path">
              <input value={form.image} onChange={(e) => set("image", e.target.value)} className={inp} />
            </Field>
            <Field label="Delivery Method">
              <input value={form.deliveryMethod} onChange={(e) => set("deliveryMethod", e.target.value)} className={inp} />
            </Field>
            <Field label="Delivery Time">
              <input value={form.deliveryTime} onChange={(e) => set("deliveryTime", e.target.value)} className={inp} />
            </Field>
            <Field label="Stock (blank = unlimited digital)">
              <input data-testid="admin-f-stock" value={form.stock} onChange={(e) => set("stock", e.target.value)} inputMode="numeric" className={inp} />
            </Field>
            <Field label="Supplier">
              <input value={form.supplier} onChange={(e) => set("supplier", e.target.value)} className={inp} />
            </Field>
            <Field label="Supplier Product ID">
              <input value={form.supplierProductId} onChange={(e) => set("supplierProductId", e.target.value)} className={inp} />
            </Field>
          </div>

          <div className="mt-4">
            <Field label="Description">
              <textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={2} className={inp} />
            </Field>
          </div>

          <div className="mt-4 rounded-2xl border border-white/[0.08] bg-card p-5">
            <p className="overline-label mb-1">Redeemable Countries *</p>
            <p className="mb-3 text-[11px] text-muted-foreground/70">
              The actual countries where this code can be redeemed. This drives marketplace visibility.
            </p>
            <div className="grid max-h-48 grid-cols-2 gap-1.5 overflow-y-auto pr-1 sm:grid-cols-3" data-testid="admin-f-redeemable">
              {store.countries.map((c) => (
                <label key={c.code} className={`flex cursor-pointer items-center gap-2 rounded-lg border px-2.5 py-1.5 text-xs transition-colors duration-150 ${form.redeemableCountries.includes(c.code) ? "border-primary/50 bg-primary/10 text-foreground" : "border-white/10 text-muted-foreground hover:border-white/25"}`}>
                  <input
                    type="checkbox"
                    data-testid={`admin-country-${c.code.toLowerCase()}`}
                    checked={form.redeemableCountries.includes(c.code)}
                    onChange={() => toggleCountry(c.code)}
                    className="h-3 w-3 accent-[#00F0FF]"
                  />
                  {c.flag} {c.name}
                </label>
              ))}
            </div>
          </div>

          <div className="mt-4 grid gap-4">
            <Field label="Redemption Instructions">
              <textarea data-testid="admin-f-instructions" value={form.redemptionInstructions} onChange={(e) => set("redemptionInstructions", e.target.value)} rows={2} className={inp} />
            </Field>
            <Field label="Restrictions (shows a region warning + requires checkout confirmation)">
              <textarea data-testid="admin-f-restrictions" value={form.restrictions} onChange={(e) => set("restrictions", e.target.value)} rows={2} className={inp} />
            </Field>
            <Field label="Terms">
              <textarea value={form.terms} onChange={(e) => set("terms", e.target.value)} rows={2} className={inp} />
            </Field>
            <Field label="Expiration">
              <input value={form.expiration} onChange={(e) => set("expiration", e.target.value)} className={inp} />
            </Field>
          </div>

          <div className="mt-5 flex flex-wrap gap-5 text-sm">
            {[
              ["availability", "Available for purchase"],
              ["featured", "Featured"],
            ].map(([k, label]) => (
              <label key={k} className="flex cursor-pointer items-center gap-2 text-muted-foreground">
                <input type="checkbox" data-testid={`admin-f-${k}`} checked={Boolean(form[k])} onChange={(e) => set(k, e.target.checked)} className="h-4 w-4 accent-[#00F0FF]" />
                {label}
              </label>
            ))}
            <label className="flex cursor-pointer items-center gap-2 text-muted-foreground">
              <input
                type="checkbox"
                data-testid="admin-f-active"
                checked={form.status === "active"}
                onChange={(e) => set("status", e.target.checked ? "active" : "inactive")}
                className="h-4 w-4 accent-[#00F0FF]"
              />
              Active
            </label>
          </div>

          {form.region === "global" && (
            <label className="mt-5 flex cursor-pointer items-start gap-3 rounded-xl border border-amber-500/30 bg-amber-500/[0.07] p-4" data-testid="admin-global-confirm-label">
              <input type="checkbox" data-testid="admin-global-confirm" checked={globalConfirm} onChange={(e) => setGlobalConfirm(e.target.checked)} className="mt-0.5 h-4 w-4 accent-[#00F0FF]" />
              <span className="text-xs leading-relaxed text-amber-200/90">
                I confirm this product is genuinely supported across every redeemable country listed
                above. Global is a deliberate classification, not a fallback.
              </span>
            </label>
          )}

          {error && (
            <p data-testid="admin-form-error" className="mt-5 rounded-lg border border-red-500/25 bg-red-500/[0.07] px-3 py-2 text-xs text-red-300">
              {error}
            </p>
          )}

          <div className="mt-7 flex gap-3">
            <button
              data-testid="admin-save-product"
              onClick={save}
              disabled={saving}
              className="flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-transform duration-200 hover:scale-[1.02] disabled:opacity-50"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              {editing === "new" ? "Create product" : "Save changes"}
            </button>
            <button
              data-testid="admin-cancel-edit"
              onClick={() => setEditing(null)}
              className="rounded-full border border-white/15 px-6 py-3 text-sm text-muted-foreground transition-colors duration-200 hover:border-white/30 hover:text-foreground"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );

}
